import {Box, Button, Grid, Typography} from "@mui/material";
import React, {useEffect} from "react";
import {useTranslation} from "react-i18next";
import {useState} from "react";
import Container from "../../../common/Container";
import {FilterPanel} from "./components";
import {DataGrid} from "@mui/x-data-grid";
import NoRowsOverlay from "../../../common/NoRowsOverlay";
import {ActionColumn, Column, columnStyle, DateColumn, DateTimeColumn, GrossColumn} from "../../../common/Columns";
import {useNavigate} from "react-router-dom";
import {lotService} from "../../../api/services/lot.service";
import {Lot, LotList, LotsFilterParams} from "../../../types/lot";
import {useCompany} from "../../../utils/useCompany";
import {useSnack} from "../../../utils/useSnack";
import BoxWithLabel from "../../../common/BoxWithLabel";
import {filterInitialState, PlansFilterParams} from "../../../types/plan";
import {planService} from "../../../api/services/plan.service";
import {useConfirmation} from "../../../context/ConfirmationModalProvider";
import {PlanState} from "../../../utils/enums";

export default function Lots() {

    const {t} = useTranslation();
    const {snack} = useSnack();
    const {company} = useCompany();
    const navigate = useNavigate();
    const [lots, setLots] = useState<LotList[]>([]);
    const [isNewOpen, setIsNewOpen] = useState(false);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(false);

    const search = async (filters: LotsFilterParams | null) => {
        try {
            setLoading(true);
            const response = await lotService.getLots(company!.id, page, pageSize, filters)
            if (response.data && response.status === 'success') {
                setLots(response.data.data);
                setTotalElements(response.data.totalElements);
            }
        } catch (error) {
            snack(t("snack.error.default"), "error")
        } finally {
            setLoading(false);
        }
    }

    const filterInitialState: LotsFilterParams = {year: null, state: null, bin: ''};
    const [filters, setFilters] = useState<LotsFilterParams>(filterInitialState);

    useEffect(() => {
        if (!company) {
            return;
        }
        search(filters)
    }, [company, filters, page, pageSize])

    const handleCreate = () => {
        navigate(`/account-lots/create`)
    }
    const {confirm} = useConfirmation();

    const handleDeleteLot = async (lotId: number) => {
        const result = await confirm({
            title: t('cabinet.lots.confirm.delete_lot.title'),
            message: (t('cabinet.lots.confirm.delete_lot.body')).replace('{lotNo}', lotId.toString()),
        });

        if (result) {
            setLoading(true);
            lotService.deleteLot(company!.id, lotId).then(async (response: any) => {
                if (response && response.data && response.status === "success") {
                    snack(t("snack.success.lot_deleted"), "success");
                    await search(filters);
                } else {
                    snack(t("snack.error.default"), "error")
                }
            }).finally(() => setLoading(false));
        } else {
            console.log("Удаление отменено!");
        }
    }

    const handleApproveLot = async (lotId: number) => {
        const result = await confirm({
            title: t('cabinet.lots.confirm.approve_lot.title'),
            message: (t('cabinet.lots.confirm.approve_lot.body')).replace('{lotNo}', lotId.toString()),
        });

        if (result) {
            setLoading(true);
            lotService.approveLot(company!.id, lotId).then(async (response: any) => {
                if (response && response.data && response.status === "success") {
                    snack(t("snack.success.lot_approved"), "success");
                    await search(filters);
                } else {
                    snack(t("snack.error.default"), "error")
                }
            }).finally(() => setLoading(false));
        } else {
            console.log("Удаление отменено!");
        }
    }
    const actions = [
        {
            label: "action.navigate",
            callback: (row: any) => {
                // Логика удаления плана. В данном примере используется подтверждение удаления.
                navigate(`/account-lots/${row.id}`);
            },
            predicate: (row: any) => {
                // Здесь можно задать условие доступности действия для конкретной записи.
                // Например, вернуть true, если удаление разрешено для данного плана.
                return true;
            }
        },
        {
            label: "action.remove",
            callback: async (row: any) => {
                // Логика удаления плана. В данном примере используется подтверждение удаления.
                await handleDeleteLot(row.id);
            },
            predicate: (row: any) => {
                // Здесь можно задать условие доступности действия для конкретной записи.
                // Например, вернуть true, если удаление разрешено для данного плана.
                return row.state === 'draft';
            }
        },
        {
            label: "action.approve",
            callback: async (row: any) => {
                // Логика удаления плана. В данном примере используется подтверждение удаления.
                await handleApproveLot(row.id);
            },
            predicate: (row: any) => {
                // Здесь можно задать условие доступности действия для конкретной записи.
                // Например, вернуть true, если удаление разрешено для данного плана.
                return row.state === PlanState.draft;
            }
        }]
    return (
        <Box>
            <Container paddingY={{xs: 2, sm: 4}} maxWidth="unset">

                <Box marginBottom={4}>
                    <Typography
                        variant="h3"
                        gutterBottom
                        sx={{
                            fontWeight: 700,
                        }}
                    >
                        {t("cabinet.lots.title")}
                    </Typography>
                </Box>


                <Grid item xs={12} sm={7} md={8} lg={9}>
                    <BoxWithLabel label={t("common.filters")}>
                        <FilterPanel
                            onSearch={search}
                            t={t}
                            initialState={filterInitialState}
                        />
                    </BoxWithLabel>
                </Grid>
                <Grid item container spacing={2} alignItems={"center"} mb={2}>
                    <Grid item xs={12} sm={5} md={4} lg={3}>
                        <Button fullWidth color="primary" variant="contained"
                                onClick={handleCreate}>
							<span style={{whiteSpace: 'nowrap'}}>
								{t("cabinet.lots.create-lot")}
							</span>
                        </Button>
                    </Grid>
                </Grid>

                <Box>
                    <DataGrid
                        components={{NoRowsOverlay: () => <NoRowsOverlay/>}}
                        sx={{
                            height: 500,
                            "& .MuiDataGrid-cell": {
                                whiteSpace: "pre-wrap"
                            },
                        }}
                        columnBuffer={4}
                        rows={lots.length
                            ? lots
                            : []
                        }
                        columns={[
                            ActionColumn(100, columnStyle, "cabinet.plans", actions),
                            Column("id", 100, columnStyle, undefined, "cabinet.lots"),
                            Column("companyName", 300, columnStyle, undefined, "cabinet.lots"),
                            Column("year", 150, columnStyle, undefined, "cabinet.lots"),
                            Column("auctionType", 200, columnStyle, "auction_type", "cabinet.lots"),
                            DateTimeColumn("auctionDate", 200, columnStyle, "cabinet.lots"),
                            DateTimeColumn("createdAt", 200, columnStyle, "cabinet.lots"),
                            Column("itemCount", 200, columnStyle, undefined, "cabinet.lots"),
                            GrossColumn(200, columnStyle, "cabinet.lots"),
                            Column("state", 200, columnStyle, "lot_state", "cabinet.lots")
                        ]}
                        loading={loading}
                        density="comfortable" style={{whiteSpace: "pre-wrap"}}
                    />
                </Box>
            </Container>
        </Box>)
}